import { useState, useContext } from 'react'
import { Eye, Edit, CircleAlert, X, Trash2, Power } from 'lucide-react'
import ProgramadoModalDetail from './ProgramadoModalDetail'
import { calculateProgramadoTotals } from './utils/calculateProgramadoTotals'
import { FacturaAPI } from '../../../../../../../../api/facturas.api'
import { Modal } from '../../../../../../ui/Modal'
import GlobalContext from '../../../../../../../../context/global-context'
import DataTable from '../../../../Inventario/components/DataTable'

const formatMoney = value =>
  Number(value || 0).toLocaleString('es-CL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

const ActionButton = ({ label, onClick, tone = 'slate', children }) => {
  const toneClass =
    tone === 'red'
      ? 'text-red-600 hover:bg-red-50'
      : tone === 'orange'
        ? 'text-orange-600 hover:bg-orange-50'
        : tone === 'green'
          ? 'text-emerald-600 hover:bg-emerald-50'
          : tone === 'blue'
            ? 'text-blue-600 hover:bg-blue-50'
            : 'text-slate-600 hover:bg-slate-100'

  return (
    <button
      type='button'
      onClick={onClick}
      className={`rounded-lg p-1.5 transition ${toneClass}`}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  )
}

export default function ProgramadosTable({ documents, onEdit, onDeleteSuccess }) {
  const { masterEntityData } = useContext(GlobalContext)
  const masterEntityId = masterEntityData?.id
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [docToDelete, setDocToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const openModal = doc => {
    setSelectedDoc(doc)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setSelectedDoc(null)
    setIsModalOpen(false)
  }

  const handleDeleteClick = doc => {
    setDocToDelete(doc)
    setIsDeleteModalOpen(true)
  }

  const closeDeleteModal = () => {
    setDocToDelete(null)
    setIsDeleteModalOpen(false)
  }

  const confirmDelete = async () => {
    if (!docToDelete) return
    setDeleting(true)
    try {
      const api = new FacturaAPI()
      await api.deleteDocumentoProgramado(docToDelete.id, masterEntityId)
      onDeleteSuccess()
      closeDeleteModal()
      if (isModalOpen) closeModal()
    } catch (error) {
      console.error('Error deleting scheduled document:', error)
    } finally {
      setDeleting(false)
    }
  }

  const handleToggleStatus = async (doc, newStatus) => {
    if (!doc) return
    try {
      const api = new FacturaAPI()
      await api.updateDocumentosProgramados(doc.id, masterEntityId, {
        status: newStatus
      })
      onDeleteSuccess()
      if (isModalOpen) closeModal()
    } catch (error) {
      console.error(
        `Error ${newStatus === 'active' ? 'activating' : 'deactivating'} scheduled document:`,
        error
      )
    }
  }

  return (
    <div className='mt-4 [&_table]:text-xs [&_thead_th]:px-3 [&_thead_th]:py-2 [&_thead_th]:text-[11px] [&_thead_th]:font-semibold [&_tbody_td]:px-3 [&_tbody_td]:py-2.5 [&_thead_th:nth-child(5)]:text-center [&_thead_th:nth-child(7)]:text-center'>
      <DataTable
        columns={[
          'Cliente',
          'Documento',
          'Frecuencia',
          'Próxima emisión',
          'Monto',
          'Estado',
          'Acciones'
        ]}
        rows={documents}
        emptyText='No hay documentos programados.'
        renderRow={doc => {
          const totals = calculateProgramadoTotals(doc)

          return (
            <>
              <td className='px-4 py-3'>
                <div className='min-w-[180px]'>
                  <p className='text-[11px] font-semibold text-slate-800'>
                    {doc.receiver_data?.name || 'Sin cliente'}
                  </p>
                  <p className='mt-0.5 font-mono text-[10px] text-slate-500'>
                    {doc.receiver_data?.tax_id || '—'}
                  </p>
                </div>
              </td>
              <td className='px-4 py-3'>
                <p className='text-xs font-medium text-slate-700'>
                  {doc.dte_type_data?.description || 'Documento'}
                </p>
                <p className='mt-0.5 text-[11px] text-slate-500'>
                  Código {doc.dte_type_data?.code || doc.dte_type || '—'}
                </p>
              </td>
              <td className='px-4 py-3'>
                <p className='text-xs font-semibold text-slate-800'>{doc.frequency_display || '—'}</p>
                <p className='mt-0.5 text-[11px] text-slate-500'>
                  {doc.execution_day || 'Sin día definido'}
                </p>
                <p className='mt-0.5 text-[11px] italic text-slate-400'>
                  {doc.end_type_display || 'Sin finalización'}
                </p>
              </td>
              <td className='px-4 py-3'>
                <p className='text-xs font-semibold text-slate-800'>{doc.next_execution || '—'}</p>
                <p className='mt-0.5 text-[11px] text-slate-500'>Inicio: {doc.start_date || '—'}</p>
              </td>
              <td className='px-4 py-3 text-center'>
                <div className='flex flex-col items-center'>
                  <p className='text-xs font-semibold text-slate-900'>
                    {formatMoney(totals.totalGeneral)} {doc.currency || ''}
                  </p>
                  {doc.currency === 'UF' && (
                    <p className='mt-0.5 text-[11px] font-medium text-orange-600'>
                      UF día {doc.currency_day || 1}
                    </p>
                  )}
                </div>
              </td>
              <td className='px-4 py-3'>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${doc.status === 'active'
                      ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                      : 'bg-slate-100 text-slate-600 ring-1 ring-slate-200'
                    }`}
                >
                  {doc.status_display || doc.status || '—'}
                </span>
              </td>
              <td className='px-4 py-3'>
                <div className='flex items-center justify-center gap-1'>
                  <ActionButton label='Ver detalle' onClick={() => openModal(doc)} tone='blue'>
                    <Eye size={14} />
                  </ActionButton>
                  <ActionButton label='Editar' onClick={() => onEdit(doc)} tone='orange'>
                    <Edit size={14} />
                  </ActionButton>
                  <ActionButton
                    label={doc.status === 'inactive' ? 'Activar' : 'Pausar'}
                    onClick={() =>
                      handleToggleStatus(doc, doc.status === 'inactive' ? 'active' : 'inactive')
                    }
                    tone={doc.status === 'inactive' ? 'green' : 'slate'}
                  >
                    <Power size={14} />
                  </ActionButton>
                  <ActionButton label='Eliminar' onClick={() => handleDeleteClick(doc)} tone='red'>
                    <Trash2 size={14} />
                  </ActionButton>
                </div>
              </td>
            </>
          )
        }}
      />

      {isModalOpen && (
        <ProgramadoModalDetail
          doc={selectedDoc}
          onClose={closeModal}
          onEdit={onEdit}
          onDelete={handleDeleteClick}
          onToggleStatus={handleToggleStatus}
        />
      )}

      <Modal isOpen={isDeleteModalOpen} onClose={closeDeleteModal}>
        <div className='bg-white rounded-2xl shadow-xl w-[450px] overflow-hidden animate-in fade-in zoom-in duration-200'>
          {/* Header */}
          <div className='flex items-center justify-between p-6 pb-2 bg-red-50/30'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-500'>
                <CircleAlert size={24} />
              </div>
              <h3 className='text-xl font-bold text-gray-800'>Eliminar Documento Programado</h3>
            </div>
            <button
              onClick={closeDeleteModal}
              className='text-gray-400 hover:text-gray-600 transition-colors'
            >
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div className='p-6 py-4'>
            <p className='text-gray-600 text-[15px] leading-relaxed'>
              ¿Estás seguro de que deseas eliminar esta programación? Esta acción no se puede
              deshacer.
            </p>
          </div>

          {/* Footer */}
          <div className='p-6 pt-2 flex justify-end gap-3'>
            <button
              onClick={closeDeleteModal}
              disabled={deleting}
              className='px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50'
            >
              Cancelar
            </button>
            <button
              onClick={confirmDelete}
              disabled={deleting}
              className='px-6 py-2.5 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors shadow-lg shadow-red-200 disabled:opacity-50 flex items-center gap-2'
            >
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </div>
      </Modal>

    </div>
  )
}
